import { useState, useEffect } from 'react';
import { trackCtaClicked } from '../utils/analytics';
import { startCheckout } from '../utils/checkout';
import { getVersionedAsset, ASSET_VERSIONS } from '../utils/constants';

const Hero = () => {
  const phrases = [
    'Researcher',
    'Engineer',
    'Chief of staff',
    'Recruiter',
    'Notetaker',
  ];

  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const [demoOpen, setDemoOpen] = useState(false);

  useEffect(() => {
    // Respect prefers-reduced-motion: the CSS kills the fade, but the swap
    // itself is JS-driven — so for motion-sensitive users we pin the first
    // phrase instead of hard-swapping the H1 every 3.2s.
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq.matches) return undefined;
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIndex((prev) => (prev === phrases.length - 1 ? 0 : prev + 1));
        setVisible(true);
      }, 400);
    }, 3200);
    return () => clearInterval(interval);
  }, [phrases.length]);

  // Lock body scroll + close the demo modal on Escape while it's open.
  useEffect(() => {
    if (!demoOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setDemoOpen(false); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [demoOpen]);

  const scrollTo = (id, label) => {
    trackCtaClicked(label, 'hero');
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const openDemo = () => {
    trackCtaClicked('watch_demo', 'hero');
    setDemoOpen(true);
  };
  const closeDemo = () => setDemoOpen(false);

  return (
    <section className="hero" id="top">
      <div className="hero-glow" aria-hidden="true" />
      <div className="hero-content">
        <p className="overline">
          <span className="overline-dot" /> A WORKSPACE FOR YOUR AI TEAM · BUILT ON CLAUDE
        </p>
        <h1 className="hero-title">
          Your AI team.
          <span className="hero-role-line">
            <span className="hero-role-arrow" aria-hidden="true">▸</span>
            <span className={`rotating-text ${visible ? 'visible' : 'hidden'}`}>
              {phrases[index]}
            </span>
          </span>
        </h1>
        <p className="hero-subtitle">
          Organize your Claude subscription into a team of AI coworkers—each scoped with its own
          permissions and skills, easy to navigate and safe to run. They even sit in on your
          meetings and pick up the work live.
        </p>

        <div className="hero-buttons">
          <button className="btn btn-primary" onClick={() => startCheckout('hero')}>
            Buy now — $25 →
          </button>
          <button className="btn btn-secondary" onClick={() => scrollTo('how-it-works', 'see_how')}>
            See how it works
          </button>
        </div>

        <p className="hero-trust">
          7-day money-back guarantee · Buy once, own forever · Runs on your own Claude
        </p>
      </div>

      <div className="hero-visual">
        <button
          type="button"
          className="hero-video-trigger"
          onClick={openDemo}
          aria-label="Play the full Solidly demo"
        >
          <video
            className="hero-video"
            src={getVersionedAsset('/videos/hero.mp4', ASSET_VERSIONS.DEMO_VIDEO)}
            poster={getVersionedAsset('/videos/hero-poster.jpg', ASSET_VERSIONS.DEMO_VIDEO)}
            autoPlay
            muted
            loop
            playsInline
          />
          <span className="hero-video-play" aria-hidden="true">▶</span>
          <span className="hero-video-label">Watch the full demo</span>
        </button>
      </div>

      {demoOpen && (
        <div
          className="video-modal-backdrop"
          onMouseDown={(e) => { if (e.target === e.currentTarget) closeDemo(); }}
        >
          <div className="video-modal" role="dialog" aria-modal="true" aria-label="Solidly full demo">
            <button className="video-modal-close" onClick={closeDemo} aria-label="Close demo">
              ✕
            </button>
            <video
              className="video-modal-player"
              src={getVersionedAsset('/videos/demo.mp4', ASSET_VERSIONS.DEMO_VIDEO)}
              poster={getVersionedAsset('/videos/demo-poster.jpg', ASSET_VERSIONS.DEMO_VIDEO)}
              controls
              autoPlay
              playsInline
            >
              {/* Captions generated by video-pipeline (compose emits the .vtt,
                  stage.py copies it next to the mp4). The hero preview loop is
                  a separate silent ambient clip with no narration to caption. */}
              <track
                kind="captions"
                src={getVersionedAsset('/videos/demo.vtt', ASSET_VERSIONS.DEMO_VIDEO)}
                srcLang="en"
                label="English"
                default
              />
            </video>
          </div>
        </div>
      )}
    </section>
  );
};

export default Hero;
